import React, { useEffect, useState } from 'react';
import { Container, Typography, FormControl, Select, MenuItem, CircularProgress, Divider, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import {
    ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
} from "recharts";
import { styled } from "@mui/material/styles";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
} from "@mui/material";

const StyledTableContainer = styled(TableContainer)({
    margin: "20px auto",
    maxWidth: "80%",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    borderRadius: "12px",
    backgroundColor: "#f5f5f5",
});

const StyledTableCell = styled(TableCell)({
    fontWeight: "bold",
    backgroundColor: "#19438C",
    color: "white",
    fontSize: "16px",
});

const StyledRowCell = styled(TableCell)({
    backgroundColor: "#e3f2fd",
    fontSize: "16px",
    padding: "12px",
});

const HighlightedRow = styled(TableRow)({
    backgroundColor: "#e8e8e8",
    fontWeight: "bold",
    fontSize: "18px",
    padding: "14px",
});

const COLORS = ['#FFBB28', '#00C49F', '#FF8042', '#0088FE', '#259c86', '#A83279', '#FFD700', '#32CD32', '#6A5ACD'];
const EXCLUDED_AGENTS = ['Erick Pineda', 'Victor Sierra', 'Kelin Ferrera']; // Agentes a excluir

const Estadisticas = () => {
    const [solicitudes, setSolicitudes] = useState([]);
    const [agentes, setAgentes] = useState([]);
    const [selectedAgente, setSelectedAgente] = useState('');
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [promedioTotal, setPromedioTotal] = useState("");
    const [promedioHoy, setPromedioHoy] = useState("");
    const [chartType, setChartType] = useState("circular");
    const [vistaAgente, setVistaAgente] = useState("individual"); // "individual" o "todos"

    useEffect(() => {
        fetch("https://chatbot.injupemp.gob.hn/reporte_tiempos_agentes/")
            .then((response) => response.json())
            .then((result) => {
                const filteredData = result.reporte_por_agente.filter(
                    (agente) => !["Erick Pineda", "Kelin Ferrera", "Victor Sierra"].includes(agente.agente_nombre)
                );
                setData(filteredData);
                setPromedioTotal(result.promedio_total);
                setPromedioHoy(result.promedio_hoy);
            })
            .catch((error) => console.error("Error fetching data:", error));
    }, []);

    useEffect(() => {
        const fetchDatos = async () => {
            try {
                const [responseTickets, responseAgentes] = await Promise.all([
                    fetch('https://chatbot.injupemp.gob.hn/obtener_tickets/'),
                    fetch('https://chatbot.injupemp.gob.hn/obtener_agentes/')
                ]);

                const dataTickets = await responseTickets.json();
                const dataAgentes = await responseAgentes.json();

                setSolicitudes(dataTickets.tickets || []);
                setAgentes(dataAgentes.agentes || []);
            } catch (error) {
                console.error('Error al cargar los datos:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDatos();
    }, []);

    // Contar solicitudes en proceso por agente
    const totalSolicitudesEnProceso = solicitudes.filter(ticket => ticket.estado.toLowerCase() === 'en proceso').length;
    const dataPorAgenteEnProceso = agentes.map(agente => {
        const cantidad = solicitudes.filter(ticket => ticket.estado.toLowerCase() === 'en proceso' && ticket.agente_asignado === agente.id).length;
        return {
            name: agente.nombre.toUpperCase(),
            value: cantidad,
            porcentaje: totalSolicitudesEnProceso > 0 ? ((cantidad / totalSolicitudesEnProceso) * 100).toFixed(2) + '%' : '0%'
        };
    }).filter(entry => entry.value > 0); // Filtrar agentes sin solicitudes en proceso

    // Filtrar agentes excluidos en el select de "Estadísticas por Agente"
    const agentesValidos = agentes.filter(agente => !EXCLUDED_AGENTS.includes(agente.nombre));

    // Contar solicitudes por agente seleccionado
    const dataPorAgente = selectedAgente
        ? [
            {
                name: "Pendientes",
                value: solicitudes.filter(ticket =>
                    ticket.estado.toLowerCase() === 'pendiente' &&
                    ticket.agente_asignado === selectedAgente
                ).length
            },
            {
                name: "En Proceso",
                value: solicitudes.filter(ticket =>
                    ticket.estado.toLowerCase() === 'en proceso' &&
                    ticket.agente_asignado === selectedAgente
                ).length
            },
            {
                name: "Cerrados",
                value: solicitudes.filter(ticket =>
                    ticket.estado.toLowerCase() === 'cerrado' &&
                    ticket.agente_asignado === selectedAgente &&
                    ticket.fecha_fin && ticket.fecha_fin.startsWith(new Date().toISOString().split('T')[0]) // Filtrar por fecha actual
                ).length
            }
        ].filter(entry => entry.value > 0)
        : [];


    // Contar solicitudes cerradas por agente (excluyendo agentes específicos)
    const contarSolicitudesCerradasPorAgente = () => {
        const solicitudesCerradasValidas = solicitudes.filter(ticket =>
            ticket.estado.toLowerCase() === 'cerrado' &&
            agentesValidos.some(agente => agente.id === ticket.agente_asignado)
        );

        const totalCerradas = solicitudesCerradasValidas.length;

        return agentesValidos.map(agente => {
            const cantidad = solicitudesCerradasValidas.filter(ticket => ticket.agente_asignado === agente.id).length;
            return {
                name: agente.nombre.toUpperCase(),
                value: cantidad,
                porcentaje: totalCerradas > 0 ? ((cantidad / totalCerradas) * 100).toFixed(2) + '%' : '0%'
            };
        }).filter(entry => entry.value > 0);
    };

    const contarSolicitudesCerradasPorAgenteBarras = () => {
        const hoy = new Date().toISOString().split('T')[0]; // Fecha actual en formato YYYY-MM-DD

        const solicitudesCerradasHoy = solicitudes.filter(ticket =>
            ticket.estado.toLowerCase() === 'cerrado' &&
            ticket.fecha_fin && ticket.fecha_fin.startsWith(hoy) && // Filtra por fecha actual
            agentesValidos.some(agente => agente.id === ticket.agente_asignado)
        );

        const totalCerradasHoy = solicitudesCerradasHoy.length;

        return agentesValidos.map(agente => {
            const cantidad = solicitudesCerradasHoy.filter(ticket => ticket.agente_asignado === agente.id).length;
            return {
                name: agente.nombre.toUpperCase(),
                value: cantidad,
                porcentaje: totalCerradasHoy > 0 ? ((cantidad / totalCerradasHoy) * 100).toFixed(2) + '%' : '0%'
            };
        }).filter(entry => entry.value > 0);
    };

    const totalPorAgente = dataPorAgente.reduce((sum, entry) => sum + entry.value, 0);
    const totalHistoricoCerradas = solicitudes.filter(ticket => ticket.estado.toLowerCase() === 'cerrado' && agentesValidos.some(agente => agente.id === ticket.agente_asignado)).length;
    const totalDiaria = solicitudes.filter(ticket =>
        ticket.estado.toLowerCase() === 'cerrado' &&
        ticket.fecha_fin && ticket.fecha_fin.startsWith(new Date().toISOString().split('T')[0])
    ).length;

    const dataHistoricoCerradas = contarSolicitudesCerradasPorAgente();
    const dataDiariasCerradas = contarSolicitudesCerradasPorAgenteBarras();
    // Ordenar los datos de mayor a menor porcentaje
    const dataOrdenada = dataHistoricoCerradas.sort((a, b) => {
        const porcentajeA = (a.value / totalHistoricoCerradas) * 100;
        const porcentajeB = (b.value / totalHistoricoCerradas) * 100;
        return porcentajeB - porcentajeA; // Orden descendente
    });

    const dataOrdenadaDiaria = dataDiariasCerradas.sort((a, b) => {
        const porcentajeA = (a.value / totalHistoricoCerradas) * 100;
        const porcentajeB = (b.value / totalHistoricoCerradas) * 100;
        return porcentajeB - porcentajeA; // Orden descendente
    });

    const opcionesFecha = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    const fechaActual = new Date().toLocaleDateString('es-ES', opcionesFecha);
    return (
        <Container style={{ marginTop: 20 }}>
            <Typography variant="h4" fontWeight="bold" align="center">
                Estadísticas de Solicitudes ChatBot INJUPEMP Virtual
            </Typography>

            {loading ? (
                <CircularProgress style={{ display: 'block', margin: '20px auto' }} />
            ) : (
                <>
                    {/* Gráfico de solicitudes en proceso por agente */}
                    <Typography variant="h6" align="center" style={{ marginTop: 20 }}>
                        Solicitudes en Proceso por Agente - {fechaActual.charAt(0).toUpperCase() + fechaActual.slice(1)}
                    </Typography>
                    <Typography variant="h6" align="center" style={{ marginTop: 20 }}>
                        Total: {totalSolicitudesEnProceso}
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={dataPorAgenteEnProceso}
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                fill="#ff6384"
                                dataKey="value"
                                label={({ name, value, porcentaje }) => `${name}: ${value} (${porcentaje})`}
                            >
                                {dataPorAgenteEnProceso.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                    <Divider />

                    {/* Filtro por agente */}
                    <Typography variant="h6" align="center" style={{ marginTop: 20 }}>
                        Estadísticas por Agente - {fechaActual.charAt(0).toUpperCase() + fechaActual.slice(1)}
                    </Typography>

                    <RadioGroup
                        row
                        value={vistaAgente}
                        onChange={(e) => setVistaAgente(e.target.value)}
                        style={{ justifyContent: "center", marginBottom: 20 }}
                    >
                        <FormControlLabel value="individual" control={<Radio />} label="Individual" />
                        <FormControlLabel value="todos" control={<Radio />} label="Todos" />
                    </RadioGroup>

                    {vistaAgente === "individual" ? (
                        <FormControl fullWidth style={{ marginBottom: 20, maxWidth: 300, margin: 'auto' }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Filtrar por agente:
                            </Typography>
                            <Select
                                value={selectedAgente}
                                onChange={(e) => setSelectedAgente(e.target.value)}
                            >
                                {agentesValidos.map(agente => (
                                    <MenuItem key={agente.id} value={agente.id}>
                                        {agente.nombre.toUpperCase()}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : null}

                    {/* Gráfico de solicitudes por agente seleccionado o todos */}
                    {vistaAgente === "individual" ? (
                        selectedAgente && (
                            <>
                                <Typography variant="h6" align="center" style={{ marginTop: 20 }}>
                                    Total: {totalPorAgente}
                                </Typography>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={dataPorAgente}
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={100}
                                            fill="#82ca9d"
                                            dataKey="value"
                                            label={({ name, value }) => `${name}: ${value}`}
                                        >
                                            {dataPorAgente.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </>
                        )
                    ) : (
                        <>
                            <Typography variant="h6" align="center" style={{ marginTop: 20 }}>
                                Total Cerradas el Día de Hoy: {totalDiaria}
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={dataOrdenadaDiaria}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="value" fill="#8884d8">
                                        {dataOrdenadaDiaria.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                        <LabelList
                                            dataKey="value"
                                            position="top"
                                            formatter={(value) => {
                                                const porcentaje = totalHistoricoCerradas > 0
                                                    ? ((value / totalHistoricoCerradas) * 100).toFixed(2)
                                                    : 0;
                                                //return `${value} (${porcentaje}%)`;
                                                return `${value}`;
                                            }}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </>
                    )}
                    <Divider style={{ marginTop: 10 }} />
                    {/* Gráfico de Histórico de Solicitudes Cerradas */}
                    <div>
                        <Typography variant="h6" align="center" style={{ marginTop: 20 }}>
                            Histórico de Solicitudes Cerradas
                        </Typography>
                        <Typography variant="h6" align="center" style={{ marginTop: 20 }}>
                            Total: {totalHistoricoCerradas}
                        </Typography>

                        <RadioGroup
                            row
                            value={chartType}
                            onChange={(e) => setChartType(e.target.value)}
                            style={{ justifyContent: "center", marginBottom: 20 }}
                        >
                            <FormControlLabel value="circular" control={<Radio />} label="Circular" />
                            <FormControlLabel value="barras" control={<Radio />} label="Barras" />
                        </RadioGroup>

                        <ResponsiveContainer width="100%" height={300}>
                            {chartType === "circular" ? (
                                <PieChart>
                                    <Pie
                                        data={dataHistoricoCerradas}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={100}
                                        fill="#FF8042"
                                        dataKey="value"
                                        label={({ name, value, porcentaje }) => `${name}: ${value} (${porcentaje})`}
                                    >
                                        {dataHistoricoCerradas.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            ) : (
                                <BarChart data={dataOrdenada}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="value" fill="#8884d8">
                                        {dataOrdenada.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                        <LabelList
                                            dataKey="value"
                                            position="top"
                                            formatter={(value) => {
                                                const porcentaje = totalHistoricoCerradas > 0
                                                    ? ((value / totalHistoricoCerradas) * 100).toFixed(2)
                                                    : 0;
                                                return `${value} (${porcentaje}%)`;
                                            }}
                                        />
                                    </Bar>
                                </BarChart>
                            )}
                        </ResponsiveContainer>
                    </div>
                    <Divider style={{ marginTop: 10 }} />

                    <Box sx={{ textAlign: "center", marginTop: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Tiempo Promedio de Resolución por Agente
                        </Typography>

                        <StyledTableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>Agente</StyledTableCell>
                                        <StyledTableCell>Tiempo Promedio de Resolución</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((agente) => (
                                        <TableRow key={agente.agente_id}>
                                            <StyledRowCell>{agente.agente_nombre}</StyledRowCell>
                                            <StyledRowCell>{agente.promedio_resolucion}</StyledRowCell>
                                        </TableRow>
                                    ))}
                                    <HighlightedRow>
                                        <TableCell >
                                            <Typography fontWeight="bold">Promedio Total:</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography fontWeight="bold">{promedioTotal}</Typography>
                                        </TableCell>
                                    </HighlightedRow>
                                    <HighlightedRow>
                                        <TableCell>
                                            <Typography fontWeight="bold">Promedio Hoy - {fechaActual.charAt(0).toUpperCase() + fechaActual.slice(1)}:</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography fontWeight="bold">{promedioHoy}</Typography>
                                        </TableCell>
                                    </HighlightedRow>
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                    </Box>
                </>
            )}
        </Container>
    );
};

export default Estadisticas;
